/**
 * Pattern editor dialog. column-mapping-ui spec, "Pattern editor with live
 * preview": the result-column preview tracks the placeholder count live as the
 * operator edits the pattern text, each column shows its captured value against
 * the real sample plus its own target-field selector, and a match count across
 * sampled rows is shown.
 *
 * The actual {1}{2}… matching algorithm is injected via the `matchPattern` /
 * `getPlaceholderNumbers` props rather than implemented here — this component
 * stays presentational. The canonical implementation lands in `@ooh/contracts`
 * as part of the transform-engine work (import-mapping-transforms capability);
 * until then callers inject their own (see the mockup's fake pattern-engine
 * adapter).
 */
import * as React from 'react';
import { ModalDialog } from '@ooh/ui/components/modal-dialog';
import { Button } from '@ooh/ui/components/button';
import { Input } from '@ooh/ui/components/input';
import { InfoHint } from '@ooh/ui/components/info-hint';
import type { FieldOptionItem } from '@ooh/contracts';
import { formatTemplate } from '@ooh/ui/lib/utils';
import { MappingFieldPicker } from './mapping-field-picker';
import {
  buildFieldOptionGroups,
  type TargetFieldSelectLabels,
} from './mapping-transforms-target-field-select';
import {
  TransformDialogHeader,
  TRANSFORM_DIALOG_MAX_WIDTH,
} from './mapping-transforms-dialog-header';
import {
  TransformModeToggle,
  type TransformMode,
  type TransformModeToggleLabels,
} from './mapping-transforms-mode-toggle';

export interface PatternMatch {
  index: number;
  value: string;
}

export type MatchPatternFn = (pattern: string, sample: string) => PatternMatch[] | null;
export type GetPlaceholderNumbersFn = (pattern: string) => number[];

export interface PatternEditorLabels extends TargetFieldSelectLabels, TransformModeToggleLabels {
  dialogTitle: string;
  /** e.g. "Kolumna:" — combined with sourceColumnLabel by the component. */
  columnLabelPrefix: string;
  /** Accessible label for the dialog-level "(i)" info hint (overall purpose + Wzór vs
   * Słownik distinction), shared with ValueMapEditorLabels since the mode toggle switches
   * between the two dialogs for the same column. Optional — TransformDialogHeader already
   * treats infoLabel/infoText as optional and skips rendering the hint when absent, which
   * keeps apps/mockups' pre-existing labels object (typed against this interface but not
   * updated for the info-button addition) compiling without a mockup-side change. */
  dialogInfoLabel?: string;
  /** Body copy for the dialog-level info hint. Optional for the same reason as dialogInfoLabel. */
  dialogInfoText?: string;
  /** Template with a `{value}` token, e.g. "Przykład: {value}" — shown above the pattern
   * input so the operator can see the raw sample they're writing a pattern against. */
  sampleValueTemplate: string;
  /** CommandInput placeholder for the searchable per-placeholder target-field picker, e.g.
   * "Szukaj pola...". Optional so apps/mockups' pre-existing labels object (typed against
   * this interface but not updated for the searchable-picker addition) keeps compiling —
   * omitting it just falls back to an empty placeholder. */
  fieldSearchPlaceholder?: string;
  /** CommandEmpty text for the searchable per-placeholder target-field picker, e.g. "Brak
   * wyników". Optional for the same mockup-compat reason as fieldSearchPlaceholder above. */
  fieldNoResults?: string;
  patternFieldLabel: string;
  autoDetectedNote: string;
  patternPlaceholder: string;
  understandingLabel: string;
  understandingInfoLabel: string;
  understandingInfoText: string;
  noPatternHint: string;
  /** Template with `{matched}` / `{total}` tokens, e.g. "Pasuje do {matched} z {total} wierszy" */
  matchedTemplate: string;
  noMatchText: string;
  showMismatchesLabel: string;
  hideMismatchesLabel: string;
  doneButtonLabel: string;
}

export interface PatternEditorDialogProps {
  sourceColumnLabel: string;
  sampleValue: string;
  initialPattern: string;
  /** Seed target field per blank index, keyed by blank number. */
  initialBlankTargets: Record<number, string | null>;
  fieldOptions: FieldOptionItem[];
  matchCount: number;
  totalCount: number;
  mismatchExamples: string[];
  matchPattern: MatchPatternFn;
  getPlaceholderNumbers: GetPlaceholderNumbersFn;
  labels: PatternEditorLabels;
  onPatternChange?: (pattern: string) => void;
  onBlankTargetChange?: (blankIndex: number, mappedTo: string | null) => void;
  /** Switches this column's transform mode — reachability affordance for value-map mode. */
  onModeChange: (mode: TransformMode) => void;
  onClose: () => void;
}

export function PatternEditorDialog({
  sourceColumnLabel,
  sampleValue,
  initialPattern,
  initialBlankTargets,
  fieldOptions,
  matchCount,
  totalCount,
  mismatchExamples,
  matchPattern,
  getPlaceholderNumbers,
  labels,
  onPatternChange,
  onBlankTargetChange,
  onModeChange,
  onClose,
}: PatternEditorDialogProps) {
  const [patternText, setPatternText] = React.useState(initialPattern);
  const [targetByIndex, setTargetByIndex] = React.useState(initialBlankTargets);
  const [showMismatches, setShowMismatches] = React.useState(false);

  const placeholderNumbers = getPlaceholderNumbers(patternText);
  const match = matchPattern(patternText, sampleValue);
  const groupedFieldOptions = React.useMemo(
    () => buildFieldOptionGroups(fieldOptions, labels),
    [fieldOptions, labels],
  );

  const handlePatternChange = (value: string) => {
    setPatternText(value);
    onPatternChange?.(value);
  };

  const handleTargetChange = (index: number, value: string | null) => {
    setTargetByIndex((prev) => ({ ...prev, [index]: value }));
    onBlankTargetChange?.(index, value);
  };

  return (
    <ModalDialog
      header={
        <TransformDialogHeader
          title={labels.dialogTitle}
          columnLabelPrefix={labels.columnLabelPrefix}
          sourceColumnLabel={sourceColumnLabel}
          infoLabel={labels.dialogInfoLabel}
          infoText={labels.dialogInfoText}
        />
      }
      onClose={onClose}
      maxWidth={TRANSFORM_DIALOG_MAX_WIDTH}
      testId="pattern-editor-dialog"
      footer={
        <Button type="button" onClick={onClose} data-testid="pattern-editor-done">
          {labels.doneButtonLabel}
        </Button>
      }
    >
      <div className="space-y-4" data-testid="pattern-editor">
        <div className="flex items-center gap-1.5">
          <TransformModeToggle mode="pattern" labels={labels} onModeChange={onModeChange} />
          <InfoHint label={labels.modeToggleInfoLabel}>{labels.modeToggleInfoText}</InfoHint>
        </div>
        {sampleValue !== '' ? (
          <p className="text-xs text-muted-foreground" data-testid="pattern-sample-value">
            {formatTemplate(labels.sampleValueTemplate, { value: sampleValue })}
          </p>
        ) : null}
        <div>
          <div className="flex items-center gap-1.5 mb-1">
            <label htmlFor="pattern-input" className="text-xs font-medium text-muted-foreground">
              {labels.patternFieldLabel}
            </label>
            <span className="text-xs text-muted-foreground/70 italic">
              {labels.autoDetectedNote}
            </span>
          </div>
          <Input
            id="pattern-input"
            value={patternText}
            onChange={(e) => handlePatternChange(e.target.value)}
            placeholder={labels.patternPlaceholder}
            className="font-mono"
            data-testid="pattern-input"
          />
        </div>

        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">
            {labels.understandingLabel}
          </span>
          <InfoHint label={labels.understandingInfoLabel}>{labels.understandingInfoText}</InfoHint>
        </div>

        {placeholderNumbers.length === 0 ? (
          <p className="text-xs text-muted-foreground italic" data-testid="pattern-no-blanks">
            {labels.noPatternHint}
          </p>
        ) : (
          <div className="space-y-2" data-testid="pattern-result-columns">
            {placeholderNumbers.map((num) => {
              const captured = match?.find((m) => m.index === num)?.value;
              return (
                <div
                  key={num}
                  className="flex items-center gap-2 text-sm"
                  data-testid={`pattern-blank-${num}`}
                >
                  <span className="font-mono text-muted-foreground w-8 shrink-0">{`{${num}}`}</span>
                  <span className="font-mono text-xs bg-muted/60 rounded px-2 py-1 min-w-[100px] text-center">
                    {captured ?? '—'}
                  </span>
                  <span className="text-muted-foreground shrink-0">→</span>
                  <div className="w-52">
                    <MappingFieldPicker
                      value={targetByIndex[num] ?? null}
                      groupedOptions={groupedFieldOptions}
                      onChange={(value) => handleTargetChange(num, value)}
                      labels={{
                        skipLabel: labels.skipLabel,
                        searchPlaceholder: labels.fieldSearchPlaceholder ?? '',
                        noResultsLabel: labels.fieldNoResults ?? '',
                      }}
                      testId={`pattern-blank-select-${num}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground" data-testid="pattern-match-summary">
          {match ? (
            <>
              {formatTemplate(labels.matchedTemplate, { matched: matchCount, total: totalCount })}
              {' · '}
              <Button
                type="button"
                variant="inline"
                className="underline underline-offset-2"
                onClick={() => setShowMismatches((v) => !v)}
                data-testid="pattern-match-show"
              >
                {showMismatches ? labels.hideMismatchesLabel : labels.showMismatchesLabel}
              </Button>
            </>
          ) : (
            labels.noMatchText
          )}
        </p>
        {showMismatches && match ? (
          <ul
            className="text-xs text-muted-foreground space-y-0.5 pl-3"
            data-testid="pattern-mismatch-list"
          >
            {mismatchExamples.map((line) => (
              <li key={line}>• {line}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </ModalDialog>
  );
}
